import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  connectionService,
  datasourceService,
  datasetGroupService,
  datasetService,
  referenceService,
} from '../services';
import { formatConnectionString, formatData, handleError, Role, Asset } from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
  PrincipalLink,
  RelationshipGraph,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useQueryParams,
  useStatus,
} from '../hooks';

export const Datasource = ({ abortFetch }) => {
  const { queryParams } = useQueryParams();

  const [datasource, setDatasource] = useState([]);
  const [datasets, setDatasets] = useState();

  const [connectionDetails, setConnectionDetails] = useState([]);
  const [datasetGroups, setDatasetGroups] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { system_id, datasource_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Datasource,
    objectIds: [datasource_id],
  });
  const { appendStatus } = useStatus({ setState: setDatasetGroups, assetCategory: Asset.System });

  useEffect(() => {
    fetchItems();
    fetchDatasetGroups();
    fetchFromReferences();
    fetchDatasets();
  }, [datasource_id]);

  const fetchItems = async () => {
    try {
      const result = await Promise.all([
        datasourceService.getById(datasource_id).catch(err => {
          throw err;
        }),
        connectionService.getFormValues().catch(err => {
          console.error(err);
        }),
      ]);

      let ds = result[0]?.datasources;
      const formValues = result[1]?.formValues;

      if (!ds || ds.length < 1) {
        setNotFound(true);
      } else {
        ds = ds[0];

        const connectorsWithUrl = ['databricks', 'snowflake'];

        let hostname = ds.datasource_hostname
          ? {
              title: 'Hostname',
              data: ds.datasource_hostname,
              isUrl: connectorsWithUrl.includes(ds.connector_code),
            }
          : {};
        let database = ds.datasource_database
          ? { title: 'Database', data: ds.datasource_database }
          : {};
        let port = ds.datasource_port ? { title: 'Port', data: ds.datasource_port } : {};
        let service = ds.datasource_service
          ? { title: 'Service', data: ds.datasource_service }
          : {};

        const formValue = formValues && formValues[ds.connector_code];
        if (formValue) {
          // A connector may label a field differently on the connection form vs. the datasource
          // detail view (e.g. the API connector's hostname is "OpenAPI Specification URL" on the
          // form but holds the API host here). `detailLabel` overrides the form `label` for this view.
          hostname = formValue.hostname.show
            ? {
                title: formValue.hostname.detailLabel || formValue.hostname.label,
                data: ds.datasource_hostname || formValue.hostname.defaultValue,
                isUrl: connectorsWithUrl.includes(ds.connector_code),
              }
            : {};
          database = formValue.database.show
            ? {
                title: formValue.database.detailLabel || formValue.database.label,
                data: ds.datasource_database || formValue.database.defaultValue,
              }
            : {};
          port = formValue.port.show
            ? {
                title: formValue.port.detailLabel || formValue.port.label,
                data:
                  ds.datasource_port || formValue.port.defaultValue || formValue.port.placeholder,
              }
            : {};
          service = formValue.service.show
            ? {
                title: formValue.service.detailLabel || formValue.service.label,
                data: ds.datasource_service || formValue.service.defaultValue,
              }
            : {};
        }

        setConnectionDetails([hostname, port, service, database]);
        setDatasource(ds);
      }
    } catch (err) {
      handleError({ err, showMessage, setNotFound });
    } finally {
      setLoading(false);
    }
  };

  const fetchDatasetGroups = async () => {
    const promise = new Promise(resolve => {
      datasetGroupService
        .getByDatasourceId(datasource_id)
        .then(res => {
          const dataset_groups =
            res &&
            res.dataset_groups.map(x => {
              return {
                ...x,
                connector_filter_name:
                  x.connector_name && x.secondary_connector_name
                    ? 'both'
                    : x.connector_name || x.secondary_connector_name || 'none',
              };
            });

          setDatasetGroups(dataset_groups);
          appendStatus();
          setLoadingTable(false);
          fetchPrincipals(dataset_groups, setDatasetGroups);
          resolve(dataset_groups);
        })
        .catch(err => {
          handleError({ err, showMessage }, () => {
            setLoadingTable(false);
          });
        });
    });

    Promise.all([promise, fetchFieldStatistics()])
      .then(result => {
        updateFieldStatistics(result[0], result[1]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchDatasets = async () => {
    const promise = new Promise(resolve => {
      datasetService
        .getByDatasourceId(datasource_id, abortFetch)
        .then(res => {
          const datasets =
            res &&
            res.datasets.map(x => {
              return {
                ...x,
                pii: x.is_pii ? 'PII' : null,
                pii_tooltip: x.is_pii
                  ? 'Contains Fields classified as Personally Identifiable Information (PII)'
                  : null,
                connector_filter_name:
                  x.connector_name && x.secondary_connector_name
                    ? 'both'
                    : x.connector_name || x.secondary_connector_name || 'none',
              };
            });

          setDatasets(datasets);
          setLoadingTable(false);
          //fetchPrincipals(datasets, setDatasets);
          resolve(datasets);
        })
        .catch(err => {
          handleError({ err, showMessage }, () => {
            setLoading(false);
          });
        });
    });

    Promise.all([promise, fetchFieldStatistics()])
      .then(result => {
        updateFieldStatistics(result[0], result[1]);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.Datasource, datasource_id)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchFieldStatistics = async () => {
    return new Promise(resolve => {
      datasetGroupService
        .getFieldStatistics(datasource_id)
        .then(res => {
          resolve(res.field_statistics);
        })
        .catch(() => {
          return null;
        });
    });
  };

  const updateFieldStatistics = (data, statistics) => {
    let statistic = {};
    let maxValue = 0;

    statistics.forEach(row => {
      if (row.dataset_count > maxValue) {
        maxValue = row.dataset_count;
      }
    });

    setDatasetGroups(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.dataset_group_id === item.dataset_group_id);
        return {
          ...item,
          dataset_count: statistic ? statistic.dataset_count : 0,
          attribute_ratio: statistic ? statistic.attribute_ratio : 0,
          dataset_count_max: maxValue,
        };
      })
    );
  };

  const addDatasetGroup = async data => {
    const dataset_groups = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .create(dataset_groups)
      .then(res => {
        fetchDatasetGroups();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {
          throw err;
        });
      });
  };

  const editDatasource = async data => {
    const datasources = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(datasources)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editDatasetGroup = async data => {
    const dataset_groups = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(dataset_groups)
      .then(async res => {
        fetchDatasetGroups();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const deleteDatasource = async data => {
    await datasourceService
      .destroy({ datasources: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/systems/${system_id}`);
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const deleteDatasetGroup = async data => {
    await datasetGroupService
      .destroy({ dataset_groups: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchDatasetGroups();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const data = loading ? [] : datasource;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Datasource"
          title={data.datasource_name}
          statusId={data.status_id}
          subtitleIcon={
            <Icon name={data.datasource_type_category} tooltip={data.datasource_type_name} />
          }
          subtitleText={
            data &&
            !loading &&
            formatConnectionString(
              data.datasource_database,
              data.datasource_hostname,
              [],
              data.datasource_type_code,
              data.datasource_type_category
            )
          }
          description={data.datasource_description}
          fromReferences={fromReferences}
          loading={loading}
          details={[
            ...connectionDetails,
            ...renderCustomPropertyValues().details,
            {
              title: 'Id',
              data: data.datasource_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editDatasource', [data], editDatasource),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteDatasource', [data], deleteDatasource),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
        >
          <div
            label="Dataset Groups"
            tabid="dataset_groups"
            resultCount={
              loadingTable || datasetGroups.length === 0 ? undefined : datasetGroups.length
            }
          >
            <DataTable
              columns={[
                {
                  id: 'dataset_group_name',
                  name: 'Name',
                  link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                  tooltip: 'dataset_group_description',
                  className: 'bold',
                },
                {
                  id: 'dataset_group_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'dataset_count',
                  name: 'Datasets',
                  type: 'chart',
                  chartType: 'bar',
                  data: { data: 'dataset_count', max: 'dataset_count_max' },
                  viewMode: 'statistics',
                },
                {
                  id: 'attribute_ratio',
                  name: 'Documentation Level',
                  type: 'chart',
                  chartType: 'percent-bar',
                  data: { data: 'attribute_ratio' },
                  viewMode: 'statistics',
                },
                ...(settings.SHOW_SYSTEM_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  onClick: items =>
                    showSideDialog('addDatasetGroup', items || selectedItems, addDatasetGroup),
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items =>
                    showSideDialog('editDatasetGroup', items || selectedItems, editDatasetGroup),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  onClick: items =>
                    showModalDialog(
                      'deleteDatasetGroup',
                      items || selectedItems,
                      deleteDatasetGroup
                    ),
                },
              ]}
              data={datasetGroups}
              idColumn="dataset_group_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editable}
              filterable={true}
              filterObjects={[
                'dataset_group_name',
                'dataset_group_description',
                ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
                'principal_name',
                'connector_filter_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div label="Relationships" tabid="relationships">
            <RelationshipGraph
              datasets={datasets}
              objectName={Asset.Datasource}
              objectId={datasource_id}
              rollupToParent
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Datasource}
              objectId={datasource_id}
              filterObjects={[
                'changedData.new_data||old_data.datasource_name',
                'changedData.new_data||old_data.datasource_description',
                'changedData.new_data||old_data.datasource_hostname',
                'changedData.new_data||old_data.datasource_database',
                'changedData.new_data||old_data.system_name',
                'changedData.new_data||old_data.datasource_type_name',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
